import React, {useState, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {
    Input,
    Toast
} from 'antd-mobile';
import {
    ScanOutlined
} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import {gql, useMutation, useQuery} from '@apollo/client';
import {
    CarrierInfoType,
    HandoverRecordType,
    HandoverScanInput
} from '../../graphql/types/HandoverTypes';
import {createRecord, ScanRecord, ResultTable, Column, insertRecordBySeq} from './components/ResultTable';
import {formatLocalizedTime} from '../../utils/dateFormatUtils';
import {parseException, parseExceptionMessageKey, showError, showRepeatedScanError} from '../../utils/exceptionUtils';
import {playScanSound, playScanFailedAoSound} from '../../utils/scanUtils';
import {isValidBarCode} from '../../utils/BarCodeUtil';
import {Header} from './components/Header';
import {Pda, PdaScanEvent} from './components/Pda';
import {ScanInput} from './components/ScanInput';
import {AutoComplete} from './components/AutoComplete';
import {useScanQueueConsumer} from './components/useScanQueueConsumer';

// 获取可用承运商列表
const GET_AVAILABLE_CARRIERS = gql`
    query GetAvailableCarriers {
        getAvailableCarriers {
            id
            name
            displayName
        }
    }
`;

// 获取当前用户绑定的仓库信息
const GET_ACCOUNT_BIND_WAREHOUSE = gql`
    query GetAccountBindWarehouse {
        warehouse: getAccountBindWarehouse {
            id
            name
        }
    }
`;

// 处理尾程交接扫描
const HANDLE_HANDOVER_SCAN = gql`
    mutation HandleHandoverScan($input: HandoverScanInput!) {
        handleHandoverScan(input: $input) {
            success
            message
            trackingNumber
            bagNumber
            carrierName
            carrierId
            carrierDisplayName
            vehicle
            scanTime
            scStages
        }
    }
`;

interface HandleScanResult {
    success: boolean;
    message?: string;
    trackingNumber?: string;
    bagNumber?: string;
    carrierName?: string;
    carrierId?: number;
    carrierDisplayName?: string;
    vehicle?: string;
    scanTime?: string;
    scStages?: string[];
}

const PageBody = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();

    // 状态管理
    const [selectedCarrier, setSelectedCarrier] = useState<number | undefined>();
    const [vehicle, setVehicle] = useState<string>('');
    const [scanValue, setScanValue] = useState<string>('');
    const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
    const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);

    // Refs for latest values in async onProcess callback
    const selectedCarrierRef = useRef(selectedCarrier);
    selectedCarrierRef.current = selectedCarrier;
    const vehicleRef = useRef(vehicle);
    vehicleRef.current = vehicle;

    // GraphQL hooks
    const {data: carriersData, loading: carriersLoading} = useQuery(GET_AVAILABLE_CARRIERS);
    const {data: warehouseData} = useQuery(GET_ACCOUNT_BIND_WAREHOUSE);
    const [handleHandoverScan] = useMutation<{ handleHandoverScan: HandleScanResult }>(HANDLE_HANDOVER_SCAN);

    // 获取当前仓库ID
    const currentWarehouseId = warehouseData?.warehouse?.id;
    const currentWarehouseIdRef = useRef(currentWarehouseId);
    currentWarehouseIdRef.current = currentWarehouseId;

    // SWX/SWIFTX 是 SwiftX 自营，不存在交接给自己的动作，从候选中剔除
    const availableCarriers: CarrierInfoType[] = (carriersData?.getAvailableCarriers ?? [])
        .filter((c: CarrierInfoType) => c.name !== 'SWIFTX');

    // 承运商选项
    const carrierOptions = availableCarriers.map((carrier: CarrierInfoType) => ({
        value: carrier.displayName || carrier.name,
        label: carrier.displayName || carrier.name
    }));

    // 根据名称获取承运商信息
    const getCarrierByName = (carrierName: string): { id: number; name: string; displayName: string } | undefined => {
        const carrier = availableCarriers.find((carrier: CarrierInfoType) =>
            (carrier.displayName || carrier.name) === carrierName
        );
        return carrier ? {
            id: carrier.id,
            name: carrier.name,
            displayName: carrier.displayName || carrier.name
        } : undefined;
    };

    // 处理承运商选择变化
    const handleCarrierChange = (value: string) => {
        const carrier = getCarrierByName(value);
        setSelectedCarrier(carrier?.id);
    };

    // 根据ID获取承运商信息
    const getCarrierById = (carrierId: number): { name: string; displayName: string } | undefined => {
        const carrier = availableCarriers.find((carrier: CarrierInfoType) => carrier.id === carrierId);
        return carrier ? {name: carrier.name, displayName: carrier.displayName || carrier.name} : undefined;
    };

    // 处理单个扫描事件
    const onProcess = async (event: PdaScanEvent) => {
        const barCode = event.code;
        const carrier = selectedCarrierRef.current;
        const currentVehicle = vehicleRef.current;
        const warehouseId = currentWarehouseIdRef.current;

        if (carrier === undefined) {
            showError({
                title: t('pda.common.scanFailed'),
                message: t('pda.common.carrierRequired')
            });
            return;
        }

        if (!warehouseId) {
            showError({
                title: t('pda.common.scanFailed'),
                message: t('errors.business.warehouse.notExist') || 'Warehouse not found'
            });
            return;
        }

        try {
            const input: HandoverScanInput = {
                warehouseId: warehouseId,
                carrierId: carrier,
                vehicle: currentVehicle.trim() || undefined,
                bagCodeOrTrackingNumber: barCode
            };

            const result = await handleHandoverScan({
                variables: {input}
            });

            const scanResult = result.data?.handleHandoverScan;

            if (scanResult?.success) {
                playScanSound();
                Toast.show(t('pda.message.succeeded') || 'Succeeded');

                const identifier = scanResult.bagNumber || scanResult.trackingNumber || barCode;
                const scStageText = scanResult.scStages?.length
                    ? ` (${scanResult.scStages.map((s: string) => t(`pda.pages.GlobalSearchPage.scStageNames.${s}`, s)).join(', ')})`
                    : '';
                setSuccessRecords(prev => insertRecordBySeq(prev, createRecord({
                    value1: scanResult.carrierDisplayName || scanResult.carrierName || '',
                    value2: `${identifier}${scStageText}`,
                    time: formatLocalizedTime(),
                    seq: event.seq
                })));

                setScanValue('');
            } else {
                const errorMessage = scanResult?.message || t('pda.common.error') || 'Unknown error';
                playScanFailedAoSound();
                showError({
                    title: t('pda.common.scanFailed'),
                    message: errorMessage
                });

                const carrierInfo = getCarrierById(carrier);
                setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                    value1: carrierInfo?.displayName || carrierInfo?.name || '',
                    value2: barCode,
                    failedReason: errorMessage,
                    time: formatLocalizedTime(),
                    seq: event.seq
                })));
            }
        } catch (error) {
            const messageKey = parseExceptionMessageKey(error);
            let errorMessage: string;

            if (messageKey === "errors.business.alreadyScanned") {
                showRepeatedScanError(error);
                const {message: msg} = parseException(error);
                errorMessage = msg;
            } else {
                const {title, message: msg} = parseException(error);
                playScanFailedAoSound();
                showError({title, message: msg});
                errorMessage = msg;
            }

            const carrierInfo = getCarrierById(carrier);
            setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                value1: carrierInfo?.displayName || carrierInfo?.name || '',
                value2: barCode,
                failedReason: errorMessage,
                time: formatLocalizedTime(),
                seq: event.seq
            })));
        }
    };

    const { enqueueScan } = useScanQueueConsumer({ maxConcurrency: 1, onProcess });

    // 处理扫描输入
    const handleScan = (barCode: string) => {
        if (isValidBarCode(barCode)) {
            enqueueScan(barCode);
        } else {
            showError({
                title: t('pda.common.scanFailed'),
                message: t('pda.business.invalidBarCode')
            });
        }
    };

    // 表格列定义
    const successColumns: Column[] = [
        {
            title: t('pda.common.carrier') || 'Carrier',
            key: 'value1'
        },
        {
            title: t('pda.common.bagPackage') || 'Bag/Package',
            key: 'value2'
        },
        {
            title: t('pda.common.time') || 'Time',
            key: 'time'
        }
    ];

    const failedColumns: Column[] = [
        {
            title: t('pda.common.carrier') || 'Carrier',
            key: 'value1'
        },
        {
            title: t('pda.common.bagPackage') || 'Bag/Package',
            key: 'value2'
        },
        {
            title: t('pda.common.failedReason') || 'Failed Reason',
            key: 'failedReason'
        },
        {
            title: t('pda.common.time') || 'Time',
            key: 'time'
        }
    ];

    return (
        <div className="flex flex-col gap-2">
            {/* 承运商选择 */}
            <div className="mb-2">
                <AutoComplete
                    className="w-full p-2 border rounded"
                    placeholder={t('pda.common.carrier') || 'Select Carrier'}
                    value={selectedCarrier !== undefined ? getCarrierById(selectedCarrier)?.displayName || getCarrierById(selectedCarrier)?.name || '' : ''}
                    onChange={handleCarrierChange}
                    options={carrierOptions}
                />
            </div>

            {/* 车辆信息 */}
            <div className="flex items-center mb-2 border rounded">
                <Input
                    className="flex-1 p-2"
                    placeholder={t('pda.pages.handover.vehiclePlaceholder') || 'Vehicle (Optional)'}
                    value={vehicle}
                    onChange={setVehicle}
                />
            </div>

            {/* 扫描输入 */}
            <ScanInput
                placeholder={t('pda.common.bagCodeTrackingNo') || 'Scan Bag Code or Tracking Number'}
                value={scanValue}
                onScan={handleScan}
            />

            {/* 结果统计 */}
            <ResultTable
                successRecords={successRecords}
                failedRecords={failedRecords}
                successColumns={successColumns}
                failedColumns={failedColumns}
            />
        </div>
    );
};

export default function HandoverScanPage() {
    const {t} = useTranslation();

    return (
        <div className="w-full h-full flex flex-col gap-2 p-2">
            <Header title={t('pda.pages.handover.handover')}/>
            <Pda/>
            <PageBody/>
        </div>
    );
}